import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { TextInput, PasswordInput, Button, Alert, Stack, Box } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "@tanstack/react-router";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await login(data.username, data.password);
      router.navigate({ to: "/" });
    } catch {
      setError("Incorrect username or password");
    }
  };

  return (
    <Box maw={400} mx="auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}
          <TextInput
            label="Username"
            {...register("username")}
            error={errors.username?.message}
            autoFocus
          />
          <PasswordInput
            label="Password"
            {...register("password")}
            error={errors.password?.message}
          />
          <Button type="submit" loading={isSubmitting} fullWidth>
            Login
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
